"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.REPORT_QUERY_KEYS = exports.METRIC_DICTIONARY_PATH = exports.COMMAND_CENTER_PATH = exports.COMMAND_CENTER_SECTIONS = exports.DRILL_ROW_LIMIT = exports.drillQuerySchema = exports.DRILL_METRICS = exports.WORKING_MINUTES_PER_DAY = exports.FUNNEL_MEASURES = exports.JOB_STAGE_COLUMN_MODULES = exports.JOB_STAGE_COLUMN_LABELS = exports.JOB_STAGE_COLUMNS = exports.COMMAND_FUNNEL_LABELS = exports.COMMAND_FUNNEL_STAGES = exports.commandCenterQuerySchema = exports.DEFAULT_REPORT_PERIOD = exports.REPORT_PERIOD_LABELS = exports.REPORT_PERIODS = exports.FISCAL_YEAR_START_MONTH = exports.METRIC_DICTIONARY = exports.METRIC_KEYS = exports.METRIC_DICTIONARY_PUBLISHED_ON = exports.METRIC_DICTIONARY_VERSION = void 0;
exports.metricDefinition = metricDefinition;
exports.setFiscalYearStartMonth = setFiscalYearStartMonth;
exports.fiscalYearStartMonth = fiscalYearStartMonth;
exports.fiscalYearOf = fiscalYearOf;
exports.fiscalQuarterOf = fiscalQuarterOf;
exports.fiscalQuarterStart = fiscalQuarterStart;
exports.fiscalQuarterMonths = fiscalQuarterMonths;
exports.fiscalYearLabel = fiscalYearLabel;
exports.monthLabel = monthLabel;
exports.commandFunnelStageOf = commandFunnelStageOf;
exports.stagesOfCommandFunnel = stagesOfCommandFunnel;
exports.jobStageColumnOf = jobStageColumnOf;
exports.stagesOfJobStageColumn = stagesOfJobStageColumn;
exports.computeFunnel = computeFunnel;
exports.median = median;
exports.toDays = toDays;
exports.elapsedDays = elapsedDays;
exports.workingDays = workingDays;
exports.shareOf = shareOf;
exports.perCount = perCount;
const zod_1 = require("zod");
const enums_1 = require("./enums");
const calendars_1 = require("./calendars");
const schemas_1 = require("./schemas");
const stage_bar_1 = require("./stage-bar");
/**
 * RPT-01 — the HR Lead command center (master plan S28), and the metric dictionary every
 * figure on it cites (D12, Appendix B, §L27). Every number is computed from the event log when
 * asked (#10); nothing here is stored.
 */
// --- The metric dictionary (Q19: a read-only page now; ADMIN-14 makes it editable) -------------
/** Version 2 (S29) adds RPT-03 to RPT-06's metrics; version 1 (S28) covered RPT-01. */
exports.METRIC_DICTIONARY_VERSION = 2;
exports.METRIC_DICTIONARY_PUBLISHED_ON = '2026-10-02';
exports.METRIC_KEYS = [
    'open_requisitions',
    'requisition_age',
    'jobs_by_stage',
    'headcount_forecast',
    'funnel',
    'conversion',
    'days_in_stage',
    'sla_breaches',
    'time_to_fill',
    'hires',
    'cost_per_hire',
    'source_effectiveness',
    'pool_sourced',
    // Version 2 (S29)
    'active_requisitions',
    'recruiter_lag',
    'time_to_fill_index',
    'screen_to_interview',
    'interview_to_offer',
    'offer_acceptance',
    'joining_rate',
    'overdue_follow_ups',
    'process_withdrawals',
    'screening_speed',
    'source_mix',
    'approval_latency',
    'decision_latency',
    'scorecard_lag',
    'panelist_no_shows',
    'reschedules_initiated',
    'requisitions_raised',
    'cancelled_after_start',
    'overrides',
    'panelist_interviews',
    'severity_index',
    'drop_off',
    'offers',
    'negotiation_rounds',
    'offer_to_join',
];
exports.METRIC_DICTIONARY = [
    {
        key: 'open_requisitions',
        name: 'Open requisitions',
        definition: 'Requisitions approved and not yet filled or cancelled: approved, job created, on hold or reopened. Counted as they stand now; the date range does not apply.',
        start: null,
        stop: null,
        exclusions: 'Drafts, requisitions still in approval, rejected, cancelled, filled and partly filled ones.',
    },
    {
        key: 'requisition_age',
        name: 'Days open (elapsed and active)',
        definition: 'Elapsed: calendar days since the requisition was approved. Active: working days in its venture or department calendar, less the working days it spent on hold.',
        start: '`requisition.approved`',
        stop: 'Now',
        exclusions: 'Active excludes weekends, holidays, hours outside the working day and time on hold (§L7).',
    },
    {
        key: 'jobs_by_stage',
        name: 'Jobs by stage',
        definition: 'Open applications on each open or paused job, by the stage they are in now.',
        start: null,
        stop: null,
        exclusions: 'Closed applications (rejected, withdrawn, auto-rejected, closed with the job, transferred).',
    },
    {
        key: 'headcount_forecast',
        name: 'Headcount forecast',
        definition: 'Per department, the headcount plan for this fiscal quarter and the next (planned and approved), beside the seats filled, vacant and in recruitment today and the requisitions open. The fiscal year starts in July (Q1 = July–September).',
        start: null,
        stop: null,
        exclusions: 'Frozen seats count as neither vacant nor in recruitment.',
    },
    {
        key: 'funnel',
        name: 'Funnel for the period',
        definition: 'What happened in the period (Q16): for each stage, the applications that entered it, and those that left it forward (advanced), rejected or withdrew. Interview rounds and holds count as one Interview stage.',
        start: '`application.created`, `application.stage_changed` in the period',
        stop: null,
        exclusions: 'Moves between interview rounds are not counted as entering Interview again. Closing with the job and transfers are not rejections.',
    },
    {
        key: 'conversion',
        name: 'Conversion',
        definition: 'Advanced ÷ entered for the stage, both counted in the period. An application that entered before the period and advanced in it counts in the advanced figure only, so a stage can show more than 100%.',
        start: null,
        stop: null,
        exclusions: 'None.',
    },
    {
        key: 'days_in_stage',
        name: 'Median days in stage',
        definition: 'The median calendar days between entering a stage and leaving it, over the stays that ended in the period.',
        start: 'Entering the stage',
        stop: 'Leaving it',
        exclusions: 'Stays still running.',
    },
    {
        key: 'sla_breaches',
        name: 'SLA breaches',
        definition: 'Time limits that passed in the period (`sla.breached`), grouped by whose court the work was in, with how many are still open. Each shows elapsed and active time.',
        start: '`sla.started`',
        stop: '`sla.breached`',
        exclusions: 'Active time excludes non-working hours and paused time (§L7).',
    },
    {
        key: 'time_to_fill',
        name: 'Time to fill',
        definition: 'From the requisition’s approval to each offer accepted on its jobs, counted when the offer was accepted in the period. Elapsed is calendar days; active is working days, less any time the requisition or its job was on hold.',
        start: '`requisition.approved`',
        stop: '`offer.accepted`',
        exclusions: 'Paused time, weekends and holidays per the calendar (Appendix B).',
    },
    {
        key: 'hires',
        name: 'Hires',
        definition: 'Offers accepted in the period (`offer.accepted`). Joining is counted separately.',
        start: null,
        stop: null,
        exclusions: 'Offers accepted and then not joined are still hires here; the joining rate counts them as no-shows.',
    },
    {
        key: 'cost_per_hire',
        name: 'Cost per hire',
        definition: 'Spend recorded in the period, per currency, divided by hires in the period. Currencies are never added together (Q17) until exchange rates exist.',
        start: null,
        stop: null,
        exclusions: 'Spend with no job is counted for its venture only.',
    },
    {
        key: 'source_effectiveness',
        name: 'Source effectiveness',
        definition: 'Applications created in the period, by source, and how far they have got since: qualified (passed screening), interviewed, hired; with the spend on that source’s postings in the period.',
        start: '`application.created` in the period',
        stop: null,
        exclusions: 'None.',
    },
    {
        key: 'pool_sourced',
        name: 'Pool-sourced hires',
        definition: 'Hires in the period whose application came from a talent pool, as a share of all hires in the period.',
        start: null,
        stop: null,
        exclusions: 'None.',
    },
    // --- Version 2 (S29): RPT-03 to RPT-06 ---
    {
        key: 'active_requisitions',
        name: 'Active requisitions (load)',
        definition: 'Requisitions the recruiter runs that are open now (approved, job created, on hold or reopened). The date range does not apply.',
        start: null,
        stop: null,
        exclusions: 'As for open requisitions.',
    },
    {
        key: 'recruiter_lag',
        name: 'Recruiter-controlled lag',
        definition: 'Per application, the working days the ball was in the recruiter’s court (`ball_in_court` on each stage change), counting only time inside the period; the figure is the median over the applications that spent any time with the recruiter in the period. Time with the hiring manager, panel, approver or candidate is never counted here (Q2).',
        start: 'Each stage change that hands the ball to the recruiter (an application starts in the recruiter’s court)',
        stop: 'The next stage change that hands it to anyone else, or the application closing',
        exclusions: 'Weekends, holidays and hours outside the job’s working calendar.',
    },
    {
        key: 'time_to_fill_index',
        name: 'Time to fill, indexed to the role family',
        definition: 'Each fill’s active time to fill divided by its role family’s benchmark: the median active time to fill for that family across the organisation over the 12 months to the period’s end (Q1). The recruiter’s index is the median of their fills’ ratios: 1.0 is typical, 1.25 is 25% slower than usual for that kind of role.',
        start: '`requisition.approved`',
        stop: '`offer.accepted`',
        exclusions: 'A family with fewer than 3 fills in the 12 months has no benchmark, and its fills are not indexed. Jobs with no role family form "Unclassified".',
    },
    {
        key: 'screen_to_interview',
        name: 'Screened → interviewed',
        definition: 'Of the applications that entered Screening in the period, the share that have since reached an interview.',
        start: 'Entering Screening in the period',
        stop: null,
        exclusions: 'None.',
    },
    {
        key: 'interview_to_offer',
        name: 'Interviewed → offered',
        definition: 'Of the applications that entered their first interview in the period, the share that have since reached Offer.',
        start: 'Entering Interview in the period',
        stop: null,
        exclusions: 'None.',
    },
    {
        key: 'offer_acceptance',
        name: 'Offer acceptance',
        definition: 'Of the offers answered in the period — accepted, declined or expired — the share accepted.',
        start: null,
        stop: '`offer.accepted`, `offer.declined`, `offer.expired`',
        exclusions: 'Offers withdrawn by the company.',
    },
    {
        key: 'joining_rate',
        name: 'Joining rate',
        definition: 'Of the accepted offers whose outcome came in the period, the share that joined; the rest did not join (no-shows), reported separately (Appendix B).',
        start: null,
        stop: 'Joining confirmed, or "Didn’t join"',
        exclusions: 'Joinings still pending.',
    },
    {
        key: 'overdue_follow_ups',
        name: 'Overdue follow-ups',
        definition: 'Tasks assigned to the person and due in the period that were completed after their due time, or are still open past it.',
        start: 'The task’s due time',
        stop: 'Completion',
        exclusions: 'Cancelled tasks.',
    },
    {
        key: 'process_withdrawals',
        name: 'Process and experience withdrawals',
        definition: 'Withdrawals in the period giving WD-PROCESS ("process took too long") or WD-EXPERIENCE ("poor experience during the process") — candidates saying the operation is failing (§L20).',
        start: null,
        stop: null,
        exclusions: 'Every other withdrawal reason.',
    },
    {
        key: 'screening_speed',
        name: 'Screening speed',
        definition: 'CVs decided in sorting mode in the period, and the median seconds a card was on screen. Medians only — never per card, never a ranking (O10).',
        start: 'The card shown',
        stop: 'The card leaving the screen',
        exclusions: 'Cards held and not decided.',
    },
    {
        key: 'source_mix',
        name: 'Source mix',
        definition: 'Applications created in the period on the person’s records, by source.',
        start: '`application.created` in the period',
        stop: null,
        exclusions: 'None.',
    },
    {
        key: 'approval_latency',
        name: 'Approval latency',
        definition: 'For each requisition approval step decided in the period, the time from the step reaching its approver to their approval. The median, elapsed and in working days.',
        start: 'The step becoming active',
        stop: '`requisition.step_approved`',
        exclusions: 'Steps still waiting; working time excludes weekends and holidays.',
    },
    {
        key: 'decision_latency',
        name: 'Decision latency',
        definition: 'For each round decision recorded in the period, the time from the round being ready to decide (every scorecard in or waived) to the decision.',
        start: '`decision.ready`',
        stop: '`decision.recorded`',
        exclusions: 'Rounds still waiting.',
    },
    {
        key: 'scorecard_lag',
        name: 'Scorecard submission lag',
        definition: 'For each scorecard first submitted in the period, the time from the interview ending to its submission.',
        start: 'The interview ending',
        stop: '`scorecard.submitted` (version 1)',
        exclusions: 'Observers’ notes; later edits.',
    },
    {
        key: 'panelist_no_shows',
        name: 'Panelist no-shows',
        definition: 'Panelists recorded absent on interviews in the period (`interview.panelist_no_show`), one per absent person.',
        start: null,
        stop: null,
        exclusions: 'Candidate no-shows, which are counted on drop-off.',
    },
    {
        key: 'reschedules_initiated',
        name: 'Reschedules initiated',
        definition: 'Interviews moved in the period at a panelist’s request (`interview.rescheduled` with initiator = panelist).',
        start: null,
        stop: null,
        exclusions: 'Moves the candidate or the recruiter asked for.',
    },
    {
        key: 'requisitions_raised',
        name: 'Requisitions raised',
        definition: 'Requisitions first submitted for approval in the period.',
        start: '`requisition.submitted`',
        stop: null,
        exclusions: 'Resubmissions after being sent back.',
    },
    {
        key: 'cancelled_after_start',
        name: 'Requisitions cancelled after work started',
        definition: 'Requisitions cancelled in the period after they had been approved — recruitment work had begun.',
        start: '`requisition.approved`',
        stop: '`requisition.cancelled`',
        exclusions: 'Drafts and requisitions cancelled while still in approval.',
    },
    {
        key: 'overrides',
        name: 'Overrides, waivers and skips',
        definition: 'Decision rules and critical-criterion blocks overridden by HR, missing scorecards waived, and pipeline stages skipped, in the period — each with who did it and their written reason (06-qol §12).',
        start: null,
        stop: null,
        exclusions: 'None.',
    },
    {
        key: 'panelist_interviews',
        name: 'Interviews conducted',
        definition: 'Interviews in the period marked completed with the person on the panel and not recorded absent.',
        start: null,
        stop: null,
        exclusions: 'Cancelled and no-show interviews.',
    },
    {
        key: 'severity_index',
        name: 'Severity index',
        definition: 'The panelist’s mean score ÷ everyone’s mean, each scorecard counted as its score’s share of its rubric’s scale, over the 12 months to today; shown only from 10 submitted scorecards (§L16). 0.90 or less reads "below average", 1.10 or more "above average". Context for calibration, never a correction.',
        start: null,
        stop: null,
        exclusions: 'Observers, scorecards with no score, panelists with fewer than 10 scorecards.',
    },
    {
        key: 'drop_off',
        name: 'Drop-off by reason',
        definition: 'Per stage, the applications rejected (a knockout auto-rejection counts as "Failed a knockout question") and withdrawn in the period from that stage, by reason, and candidate no-shows (interviews missed, offers accepted and not joined); each as a share of the stage’s entries in the period. WD-PROCESS and WD-EXPERIENCE are highlighted.',
        start: '`application.stage_changed` to rejected or withdrawn in the period',
        stop: null,
        exclusions: 'Duplicates merged away (GEN-DUPLICATE) and incomplete applications (SCR-INCOMPLETE) are data hygiene, not decisions (§L19).',
    },
    {
        key: 'offers',
        name: 'Offers made, accepted, declined, expired',
        definition: 'Offers sent to the candidate in the period (`offer.sent`, first send), and offers accepted, declined (by §L20 reason) and expired in the period.',
        start: null,
        stop: null,
        exclusions: 'Offers withdrawn by the company before sending.',
    },
    {
        key: 'negotiation_rounds',
        name: 'Negotiation rounds',
        definition: 'For offers answered in the period, how many times the offer was revised (`offer.revised`); the median.',
        start: null,
        stop: null,
        exclusions: 'None.',
    },
    {
        key: 'offer_to_join',
        name: 'Offer to join',
        definition: 'For joinings confirmed in the period, calendar days from the offer being accepted to joining; the median (Appendix B "time to slot").',
        start: '`offer.accepted`',
        stop: 'Joining confirmed',
        exclusions: 'No-shows.',
    },
];
function metricDefinition(key) {
    const found = exports.METRIC_DICTIONARY.find((metric) => metric.key === key);
    if (!found)
        throw new Error(`No metric definition for ${key}`);
    return found;
}
// --- Fiscal year (Q15, O2: July) --------------------------------------------------------------
/** S28 Q15's July — the default; ADMIN-15 (S31) may change it, and every helper below follows. */
exports.FISCAL_YEAR_START_MONTH = 7;
let fiscalStartMonth = exports.FISCAL_YEAR_START_MONTH;
/** Set from the organisation settings (S31): the API on boot and on every change, the browser at sign-in. */
function setFiscalYearStartMonth(month) {
    if (Number.isInteger(month) && month >= 1 && month <= 12)
        fiscalStartMonth = month;
}
function fiscalYearStartMonth() {
    return fiscalStartMonth;
}
const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
/** Fiscal year N starts in its start month of year N (July by default) — the year it starts in. */
function fiscalYearOf(year, month) {
    return month >= fiscalStartMonth ? year : year - 1;
}
/** 1–4: July–September is Q1. */
function fiscalQuarterOf(month) {
    const offset = (month - fiscalStartMonth + 12) % 12;
    const quarter = Math.floor(offset / 3) + 1;
    if (quarter === 1 || quarter === 2 || quarter === 3 || quarter === 4)
        return quarter;
    throw new Error(`No fiscal quarter for month ${month}`);
}
/** The calendar year and month (1–12) a fiscal quarter starts in. */
function fiscalQuarterStart(fiscalYear, quarter) {
    const month = fiscalStartMonth + (quarter - 1) * 3;
    return month > 12 ? { year: fiscalYear + 1, month: month - 12 } : { year: fiscalYear, month };
}
/** "Jul–Sep". */
function fiscalQuarterMonths(quarter) {
    const { month } = fiscalQuarterStart(2000, quarter);
    return `${MONTH_NAMES[month - 1]}–${MONTH_NAMES[((month + 1) % 12)]}`;
}
/** "FY 2026–27" — or "FY 2026" when the fiscal year is the calendar year. */
function fiscalYearLabel(fiscalYear) {
    if (fiscalStartMonth === 1)
        return `FY ${fiscalYear}`;
    return `FY ${fiscalYear}–${String((fiscalYear + 1) % 100).padStart(2, '0')}`;
}
function monthLabel(month) {
    return MONTH_NAMES[month - 1] ?? String(month);
}
// --- Filters (F17.03) -------------------------------------------------------------------------
exports.REPORT_PERIODS = ['this_month', 'last_month', 'this_quarter', 'last_quarter', 'this_fiscal_year', 'last_30_days', 'custom'];
exports.REPORT_PERIOD_LABELS = {
    this_month: 'This month',
    last_month: 'Last month',
    this_quarter: 'This quarter',
    last_quarter: 'Last quarter',
    this_fiscal_year: 'This fiscal year',
    last_30_days: 'Last 30 days',
    custom: 'Custom dates',
};
exports.DEFAULT_REPORT_PERIOD = 'this_month';
exports.commandCenterQuerySchema = zod_1.z.object({
    period: zod_1.z.enum(exports.REPORT_PERIODS).default(exports.DEFAULT_REPORT_PERIOD),
    from: calendars_1.isoDateSchema.optional(),
    to: calendars_1.isoDateSchema.optional(),
    ventureId: schemas_1.uuidSchema.optional(),
    departmentId: schemas_1.uuidSchema.optional(),
    recruiterUserId: schemas_1.uuidSchema.optional(),
    /** S31 QA 2 / Q21: also show spend and cost per hire converted into the reporting currency. */
    convert: zod_1.z.enum(['1']).optional(),
});
// --- The funnel (RPT-01b, Q16) ----------------------------------------------------------------
exports.COMMAND_FUNNEL_STAGES = ['applied', 'screening', 'assessment', 'interview', 'offer', 'hired', 'joined'];
exports.COMMAND_FUNNEL_LABELS = {
    applied: 'Applied',
    screening: 'Screened',
    assessment: 'Tested',
    interview: 'Interviewed',
    offer: 'Offered',
    hired: 'Hired',
    joined: 'Joined',
};
const FUNNEL_STAGE_OF = {
    new: 'applied',
    screening: 'screening',
    assessment: 'assessment',
    interview_1: 'interview',
    interview_2: 'interview',
    interview_3: 'interview',
    hold: 'interview',
    offer: 'offer',
    hired_pending_join: 'hired',
    joined: 'joined',
};
function commandFunnelStageOf(stage) {
    return FUNNEL_STAGE_OF[stage] ?? null;
}
/** The pipeline stages one funnel stage stands for — a jobs-by-stage cell's records. */
function stagesOfCommandFunnel(stage) {
    return enums_1.APPLICATION_STAGES.filter((key) => FUNNEL_STAGE_OF[key] === stage);
}
// --- Jobs by stage (RPT-01): the stage bar's stages (§L33), as they stand now ------------------
/**
 * The §L33 stages that hold people, in the bar's order. Interview rounds and holds are one
 * Interview column, as in RPT-01b (Q16): a table across jobs with different round counts.
 * Selected is `offer` and later whether Offers is on or off (§L33 ✓).
 */
exports.JOB_STAGE_COLUMNS = ['review', 'shortlist', 'contact', 'assessment', 'interview', 'selected'];
exports.JOB_STAGE_COLUMN_LABELS = {
    review: 'Review',
    shortlist: 'Shortlist',
    contact: 'Contact',
    assessment: 'Assessment',
    interview: 'Interview',
    selected: 'Selected',
};
/** §L34: the switchable module a column belongs to. A column of a switched-off module is not shown (§L28). */
exports.JOB_STAGE_COLUMN_MODULES = { assessment: 'assessments' };
const JOB_STAGE_COLUMN_STAGES = {
    review: ['screening'],
    shortlist: ['screening'],
    contact: ['screening'],
    assessment: ['assessment'],
    interview: ['interview_1', 'interview_2', 'interview_3', 'hold'],
    selected: stage_bar_1.SELECTED_STAGES,
};
/** Which column an open application counts in, by the same rule as the stage bar; null for none (`new`). */
function jobStageColumnOf(row) {
    // §L18's hold is the backup after the rounds: not on the bar, but still in the Interview stage (Q16).
    if (row.stage === 'hold')
        return 'interview';
    const key = (0, stage_bar_1.stageBarKeyOf)(row);
    if (key === 'interview_1' || key === 'interview_2' || key === 'interview_3')
        return 'interview';
    return exports.JOB_STAGE_COLUMNS.find((column) => column === key) ?? null;
}
/** The pipeline stages a column draws from; Review, Shortlist and Contact then split `screening` by its statuses. */
function stagesOfJobStageColumn(column) {
    return JOB_STAGE_COLUMN_STAGES[column];
}
const REJECTED_STAGES = new Set(['rejected', 'auto_rejected']);
exports.FUNNEL_MEASURES = ['entered', 'advanced', 'rejected', 'withdrawn'];
const DAY_MS = 24 * 60 * 60 * 1000;
/**
 * Q16's funnel: what happened in [from, to). Pure, so QA 1's hand count can be checked against
 * it with nothing but a list of moves. A move counts once per stage group, so interview round 1 →
 * round 2 is neither an exit nor an entry.
 */
function computeFunnel(moves, from, to) {
    const cells = new Map(exports.COMMAND_FUNNEL_STAGES.map((stage) => [stage, { ids: { entered: new Set(), advanced: new Set(), rejected: new Set(), withdrawn: new Set() }, stays: [] }]));
    const order = (stage) => exports.COMMAND_FUNNEL_STAGES.indexOf(stage);
    const byApplication = new Map();
    for (const move of moves)
        byApplication.set(move.applicationId, [...(byApplication.get(move.applicationId) ?? []), move]);
    for (const [applicationId, own] of byApplication) {
        let enteredAt = null;
        for (const move of [...own].sort((a, b) => a.at - b.at)) {
            const before = move.from === null ? null : commandFunnelStageOf(move.from);
            const after = commandFunnelStageOf(move.to);
            const inPeriod = move.at >= from && move.at < to;
            if (before !== null && after !== before) {
                const cell = cells.get(before);
                if (cell && inPeriod) {
                    if (REJECTED_STAGES.has(move.to))
                        cell.ids.rejected.add(applicationId);
                    else if (move.to === 'withdrawn')
                        cell.ids.withdrawn.add(applicationId);
                    else if (after !== null && order(after) > order(before))
                        cell.ids.advanced.add(applicationId);
                    if (enteredAt !== null)
                        cell.stays.push(move.at - enteredAt);
                }
            }
            if (after !== null && after !== before) {
                if (inPeriod)
                    cells.get(after)?.ids.entered.add(applicationId);
                enteredAt = move.at;
            }
            if (after === null)
                enteredAt = null;
        }
    }
    return exports.COMMAND_FUNNEL_STAGES.map((stage) => {
        const cell = cells.get(stage);
        return { stage, applicationIds: cell?.ids ?? { entered: new Set(), advanced: new Set(), rejected: new Set(), withdrawn: new Set() }, stays: cell?.stays ?? [] };
    });
}
/** The middle value; the mean of the two middle ones for an even count. Null for none. */
function median(values) {
    if (values.length === 0)
        return null;
    const sorted = [...values].sort((a, b) => a - b);
    const middle = Math.floor(sorted.length / 2);
    const value = sorted.length % 2 === 1 ? sorted[middle] : ((sorted[middle - 1] ?? 0) + (sorted[middle] ?? 0)) / 2;
    return value ?? null;
}
/** Days to one decimal place — the precision every duration on RPT-01 shows. */
function toDays(milliseconds) {
    return Math.round((milliseconds / DAY_MS) * 10) / 10;
}
/** D14's default working day (09:45–18:45): the unit "active days" are read in, as on every SLA display. */
exports.WORKING_MINUTES_PER_DAY = 9 * 60;
/** Calendar minutes as days, and working minutes as working days, to one decimal place. */
function elapsedDays(minutes) {
    return Math.round((minutes / (24 * 60)) * 10) / 10;
}
function workingDays(minutes) {
    return Math.round((minutes / exports.WORKING_MINUTES_PER_DAY) * 10) / 10;
}
/** A share as 0–1, or null when there is nothing to divide by. */
function shareOf(part, whole) {
    return whole > 0 ? part / whole : null;
}
/** Q17: spend in one currency over a count, in whole minor units; never across currencies. */
function perCount(amountMinor, count) {
    if (count <= 0)
        return null;
    const whole = BigInt(count);
    return (amountMinor * 2n + whole) / (whole * 2n);
}
// --- Drill-through (F17.02: "every number opens its underlying records") ----------------------
exports.DRILL_METRICS = ['open_requisitions', 'jobs_by_stage', 'funnel', 'sla_breaches', 'time_to_fill', 'hires', 'cost_per_hire', 'source_effectiveness', 'pool_sourced'];
exports.drillQuerySchema = exports.commandCenterQuerySchema.extend({
    metric: zod_1.z.enum(exports.DRILL_METRICS),
    /** Which figure: "interview:entered", "<jobId>:selected", "linkedin:hired", "BDT"… */
    key: zod_1.z.string().trim().max(120).default(''),
});
/** Rows a drill-through returns; its count is always the full number. */
exports.DRILL_ROW_LIMIT = 500;
/** N-70's landing and the morning digest's: the section anchors on RPT-01. */
exports.COMMAND_CENTER_SECTIONS = ['requisitions', 'stages', 'forecast', 'funnel', 'breaches', 'time-to-fill', 'cost', 'sources'];
exports.COMMAND_CENTER_PATH = '/reports/command-center';
exports.METRIC_DICTIONARY_PATH = '/reports/metrics';
exports.REPORT_QUERY_KEYS = [['command-center'], ['report-drill']];
//# sourceMappingURL=reports.js.map